import axios from '../config/axios';

export interface BillingDifference {
  invoice_id: string;
  invoice_name: string;
  invoice_status: string;
  invoice_date: string;
  invoiceline_id: string;
  invoiceline_name: string;
  contractline_id: string;
  billing_cycle: string;
  billing_period: {
    start: string;
    actual_end: string;
    expected_end: string;
  };
  difference_days: number;
  line_total: number;
  currency: string;
  discrepancy_type: 'OFF_BY_ONE_ERROR' | 'TOO_LONG' | 'TOO_SHORT';
}

export interface BillingDifferencesSummary {
  total_invoices_checked: number;
  total_lines_checked: number;
  differences_found: number;
  discrepancy_rate: number;
  errors_count: number;
}

export interface BillingDifferencesStatistics {
  discrepancy_types: Record<string, number>;
  status_breakdown?: Record<string, {
    lines: number;
    differences: number;
  }>;
}

export interface BillingDifferencesResponse {
  success: boolean;
  summary: BillingDifferencesSummary;
  statistics: BillingDifferencesStatistics;
  differences: BillingDifference[];
  errors?: Array<{
    invoice_name: string;
    invoiceline_name: string;
    error: string;
  }>;
  filters: {
    include_deleted: boolean;
    status_filter?: string[];
    limit?: number;
  };
}

export interface BillingDateFixRequest {
  apply_fixes: boolean;
}

export interface BillingDateFixResponse {
  success: boolean;
  total_invoices_checked: number;
  total_lines_checked: number;
  total_lines_fixed: number;
  invoices_with_fixes: number;
  mode: 'LIVE_UPDATE' | 'DRY_RUN';
  invoices: Array<{
    invoice_id: string;
    invoice_name: string;
    invoice_status: string;
    lines_checked: number;
    lines_fixed: number;
    lines_with_errors: number;
    lines: Array<{
      invoiceline_id: string;
      invoiceline_name: string;
      billing_period_start: string;
      billing_period_end: string;
      current_end: string;
      billing_cycle: string;
      incorrect_end: string;
      correct_end: string;
      status: 'ok' | 'needs_fix' | 'unknown' | 'error';
      error_message?: string;
    }>;
  }>;
  errors?: string[];
}

export interface InvoiceLineUpdate {
  invoiceline_id: string;
  line_billing_start?: string;
  line_billing_end: string;
  invoiceline_updatedby: string;
  invoiceline_updatedat?: string;
}

export interface BulkInvoiceLineUpdate {
  updates: InvoiceLineUpdate[];
}

class BillingDateService {
  private baseUrl = '/api/v1';

  /**
   * Get list of all billing date differences
   */
  async getBillingDifferences(
    includeDeleted: boolean = false,
    statusFilter?: string[],
    limit?: number
  ): Promise<BillingDifferencesResponse> {
    const params = new URLSearchParams();
    params.append('include_deleted', includeDeleted.toString());
    
    if (statusFilter && statusFilter.length > 0) {
      statusFilter.forEach(status => params.append('status_filter', status));
    }
    
    if (limit) {
      params.append('limit', limit.toString());
    }

    const response = await axios.get(`${this.baseUrl}/billing-differences/list?${params.toString()}`);
    return response.data;
  }

  /**
   * Get summary of billing date differences
   */
  async getBillingDifferencesSummary(includeDeleted: boolean = false): Promise<{
    success: boolean;
    summary: BillingDifferencesSummary;
    statistics: {
      discrepancy_types: Record<string, number>;
      status_breakdown: Record<string, {
        lines: number;
        differences: number;
      }>;
    };
    filters: {
      include_deleted: boolean;
    };
  }> {
    const params = new URLSearchParams();
    params.append('include_deleted', includeDeleted.toString());

    const response = await axios.get(`${this.baseUrl}/billing-differences/summary?${params.toString()}`);
    return response.data;
  }

  /**
   * Check and optionally fix billing dates using the existing endpoint
   */
  async checkAndFixBillingDates(applyFixes: boolean = false): Promise<BillingDateFixResponse> {
    const response = await axios.post(`${this.baseUrl}/billing/check-dates`, {
      apply_fixes: applyFixes
    });
    return response.data;
  }

  /**
   * Update a single invoice line's billing end date
   *
   * Uses the bulk-update endpoint with a single update payload to align with
   * existing backend routes and avoid 404s on non-existent single-line routes.
   */
  async updateInvoiceLineBillingDate(
    invoiceLineId: string,
    newStartDate: string | undefined,
    newEndDate: string,
    updatedBy: string = 'billing_date_updater'
  ): Promise<{ success: boolean; message: string }> {
    const updatePayload: InvoiceLineUpdate = {
      invoiceline_id: invoiceLineId,
      line_billing_end: newEndDate,
      invoiceline_updatedby: updatedBy,
      invoiceline_updatedat: new Date().toISOString()
    };
    if (newStartDate) {
      updatePayload.line_billing_start = newStartDate;
    }

    const response = await axios.post(`${this.baseUrl}/invoice-lines/bulk-update`, {
      updates: [updatePayload]
    });

    // Normalize response to match the expected return type
    // Backend bulk endpoint typically returns success/failed counts and results.
    const data = response.data as any;

    if (data && typeof data.success === 'boolean') {
      // If backend already returns a success flag and message-like info, map it.
      const message =
        data.message ||
        (data.updated_count === 1 && data.failed_count === 0
          ? 'Invoice line billing date updated successfully'
          : 'Bulk invoice line update completed');

      return { success: data.success, message };
    }

    // Fallback: assume success if no explicit contract is provided
    return { success: true, message: 'Invoice line billing date updated' };
  }

  /**
   * Bulk update multiple invoice lines' billing end dates
   */
  async bulkUpdateInvoiceLineBillingDates(
    updates: InvoiceLineUpdate[]
  ): Promise<{ 
    success: boolean; 
    updated_count: number; 
    failed_count: number; 
    results: Array<{
      invoiceline_id: string;
      success: boolean;
      error?: string;
    }>;
  }> {
    const response = await axios.post(`${this.baseUrl}/invoice-lines/bulk-update`, {
      updates
    });
    return response.data;
  }

  /**
   * Get invoice line details for verification
   */
  async getInvoiceLine(invoiceLineId: string): Promise<{
    invoiceline_id: string;
    invoiceline_name: string;
    line_billing_start: string;
    line_billing_end: string;
    billing_cycle: string;
    invoice_name: string;
    invoice_status: string;
  }> {
    const response = await axios.get(`${this.baseUrl}/invoice-lines/${invoiceLineId}`);
    return response.data;
  }

  /**
   * Calculate expected end date for a given start date and billing cycle
   */
  calculateExpectedEndDate(startDate: string, billingCycle: string): string {
    // Normalize to UTC midnight to avoid timezone edge cases and ensure
    // month-based arithmetic matches backend billing period expectations.
    const [year, month, day] = startDate.split('-').map(Number);
    const start = new Date(Date.UTC(year, month - 1, day));
    let expectedEnd: Date;

    switch (billingCycle.toLowerCase()) {
      case '1_month':
      case 'monthly':
        expectedEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, start.getUTCDate()));
        break;
      case '3_months':
      case 'quarterly':
        expectedEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 3, start.getUTCDate()));
        break;
      case '6_months':
      case 'semi_annual':
        expectedEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 6, start.getUTCDate()));
        break;
      case '12_months':
      case 'annual':
      case 'yearly':
        expectedEnd = new Date(Date.UTC(start.getUTCFullYear() + 1, start.getUTCMonth(), start.getUTCDate()));
        break;
      default:
        // Default to monthly
        expectedEnd = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, start.getUTCDate()));
    }

    // Subtract 1 day to get the correct inclusive end date for the period.
    expectedEnd.setUTCDate(expectedEnd.getUTCDate() - 1);

    return expectedEnd.toISOString().split('T')[0];
  }

  /**
   * Validate a billing period
   */
  validateBillingPeriod(startDate: string, endDate: string, billingCycle: string): {
    isValid: boolean;
    expectedEndDate: string;
    actualDifference: number;
    message: string;
  } {
    const expectedEndDate = this.calculateExpectedEndDate(startDate, billingCycle);
    const actualEnd = new Date(endDate);
    const expectedEnd = new Date(expectedEndDate);
    
    const actualDifference = Math.floor((actualEnd.getTime() - expectedEnd.getTime()) / (1000 * 60 * 60 * 24));
    const isValid = actualDifference === 0;

    let message = '';
    if (isValid) {
      message = 'Billing period is correct';
    } else if (actualDifference > 0) {
      message = `Billing period is ${actualDifference} day(s) too long`;
    } else {
      message = `Billing period is ${Math.abs(actualDifference)} day(s) too short`;
    }

    return {
      isValid,
      expectedEndDate,
      actualDifference,
      message
    };
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * Format currency amount
   */
  formatCurrency(amount: number, currency: string = 'EUR'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  }
}

export const billingDateService = new BillingDateService();
export default billingDateService;